import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, ArrowRight, Clock3, Loader2, MessageSquareText, RefreshCcw, Sparkles, X } from 'lucide-react';
import { SupportErrorMessage } from '../overlays/EngineeringSupportBanner';
import type {
  IntellioAssistantConversationMessageRecord,
  IntellioAssistantConversationSessionRecord,
} from '@triedge/domain-core';

const TWO_PANE_MIN_WIDTH = 760;

const formatTimestamp = (value: number | null | undefined): string => {
  if (!Number.isFinite(value)) {
    return 'Just now';
  }
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(Number(value) * 1000);
};

const classifyMessage = (message: IntellioAssistantConversationMessageRecord) => {
  const metadata = message.metadata && typeof message.metadata === 'object'
    ? message.metadata as Record<string, unknown>
    : null;
  if (metadata?.artifactPreview) {
    return 'Artifact moment';
  }
  if (metadata?.taskThread) {
    return 'Task checkpoint';
  }
  if (message.role === 'user') {
    return 'Prompt';
  }
  return 'Assistant';
};

const getPreferredThreadAnchor = (
  session: IntellioAssistantConversationSessionRecord,
): string | null => session.firstUserMessageId ?? session.lastMessageId ?? null;

type AssistantThreadsBrowserProps = {
  sessions: IntellioAssistantConversationSessionRecord[];
  currentSessionId: string | null;
  selectedSessionId: string | null;
  selectedTimeline: {
    session: IntellioAssistantConversationSessionRecord;
    messages: IntellioAssistantConversationMessageRecord[];
  } | null;
  selectedTimelineStatus?: 'idle' | 'loading' | 'ready' | 'error';
  selectedTimelineErrorMessage?: string | null;
  onRetrySelectedTimeline?: ((sessionId: string) => void) | null;
  onSelectSession: (sessionId: string) => void;
  onOpenInChat: (sessionId: string, messageId?: string | null) => void;
};

export const AssistantThreadsBrowser: React.FC<AssistantThreadsBrowserProps> = ({
  sessions,
  currentSessionId,
  selectedSessionId,
  selectedTimeline,
  selectedTimelineStatus = 'idle',
  selectedTimelineErrorMessage = null,
  onRetrySelectedTimeline = null,
  onSelectSession,
  onOpenInChat,
}) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [isTwoPane, setIsTwoPane] = useState(false);
  const [drawerSessionId, setDrawerSessionId] = useState<string | null>(null);

  useEffect(() => {
    const node = rootRef.current;
    if (!node || typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      setIsTwoPane(width >= TWO_PANE_MIN_WIDTH);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isTwoPane) {
      setDrawerSessionId(null);
    }
  }, [isTwoPane]);

  const selectedSession = useMemo(
    () => sessions.find((session) => session.id === selectedSessionId) ?? selectedTimeline?.session ?? null,
    [selectedSessionId, selectedTimeline?.session, sessions],
  );
  const selectedTimelineMessages = selectedTimeline?.messages ?? [];
  const selectedTimelineViewState = useMemo<'unselected' | 'loading' | 'ready' | 'empty' | 'error'>(() => {
    if (!selectedSession) {
      return 'unselected';
    }
    if (selectedTimelineStatus === 'error') {
      return 'error';
    }
    if (selectedTimelineStatus === 'loading') {
      return 'loading';
    }
    if (selectedTimelineMessages.length > 0) {
      return 'ready';
    }
    if (selectedTimelineStatus === 'ready' || selectedSession.messageCount === 0) {
      return 'empty';
    }
    return 'loading';
  }, [selectedSession, selectedTimelineMessages.length, selectedTimelineStatus]);

  const detailPane = (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[24px] border border-slate-200/80 bg-white p-4 shadow-[0_18px_44px_rgba(36,50,53,0.06)]">
      {selectedSession ? (
        <>
          <div className="shrink-0 border-b border-slate-200/80 pb-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary/70">Thread detail</p>
                <h3 className="mt-1 truncate text-sm font-semibold text-slate-900">
                  {selectedSession.title ?? selectedSession.routeLabel ?? selectedSession.route}
                </h3>
                <p className="mt-1 text-[12px] text-irongrey/58">
                  {selectedSession.messageCount} message{selectedSession.messageCount === 1 ? '' : 's'} · {formatTimestamp(selectedSession.updatedAt)}
                </p>
              </div>
              <button
                type="button"
                className="shrink-0 rounded-full border border-primary/15 bg-primary/5 px-3 py-1.5 text-[12px] font-semibold text-primary transition-colors hover:bg-primary hover:text-white"
                onClick={() => onOpenInChat(selectedSession.id, getPreferredThreadAnchor(selectedSession))}
                data-testid="ai-threads-open-in-chat"
              >
                Open in chat
              </button>
            </div>
            <div
              className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-irongrey/62"
              data-testid="ai-threads-browser-session-summary"
            >
              <span className="rounded-full border border-slate-200 bg-[#f8faf9] px-2 py-1 font-medium text-irongrey/72">
                {selectedSession.routeLabel ?? selectedSession.route}
              </span>
              <span className="rounded-full border border-slate-200 bg-[#f8faf9] px-2 py-1 font-medium text-irongrey/72">
                {selectedSession.status === 'open' ? 'Current session available' : 'Persisted session'}
              </span>
            </div>
          </div>

          <div className="mt-4 min-h-0 flex-1 overflow-y-auto pr-1">
            <div className="min-w-0">
              <div className="space-y-3" data-testid={isTwoPane ? 'ai-threads-browser-detail-pane' : 'ai-threads-browser-detail-drawer'}>
                {selectedTimelineViewState === 'loading' ? (
                  <div
                    className="rounded-[20px] border border-primary/15 bg-[linear-gradient(135deg,rgba(224,247,244,0.92),rgba(255,255,255,0.98))] px-4 py-4 text-sm text-irongrey/72"
                    data-testid="ai-threads-browser-detail-loading"
                  >
                    <div className="flex items-center gap-2 text-primary">
                      <Loader2 size={16} className="animate-spin motion-reduce:animate-none" />
                      <span className="font-semibold">Loading persisted activity…</span>
                    </div>
                    <p className="mt-2 leading-6 text-irongrey/68">
                      Session messages are being hydrated for this thread. This state must resolve without a page refresh.
                    </p>
                  </div>
                ) : null}
                {selectedTimelineViewState === 'error' ? (
                  <SupportErrorMessage
                    data-testid="ai-threads-browser-detail-error"
                  >
                    <div className="flex items-center gap-2 font-semibold">
                      <AlertCircle size={16} />
                      <span>Persisted activity could not be loaded.</span>
                    </div>
                    <p className="mt-2 leading-6">
                      {selectedTimelineErrorMessage ?? 'The timeline request failed before the persisted session activity could be displayed.'}
                    </p>
                    {onRetrySelectedTimeline ? (
                      <button
                        type="button"
                        className="mt-3 inline-flex items-center gap-2 rounded-full border border-[#f2c6b3] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#8b4d2d] transition-colors hover:bg-[#fff0e7]"
                        onClick={() => onRetrySelectedTimeline(selectedSession.id)}
                        data-testid="ai-threads-browser-detail-retry"
                      >
                        <RefreshCcw size={14} />
                        Retry timeline
                      </button>
                    ) : null}
                  </SupportErrorMessage>
                ) : null}
                {selectedTimelineViewState === 'ready' ? selectedTimelineMessages.map((message) => (
                  <article
                    key={`${message.sessionId}:${message.messageId}`}
                    className="rounded-[20px] border border-slate-200/80 bg-[#f8faf9] px-4 py-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-primary/70">{classifyMessage(message)}</p>
                        <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-slate-900">{message.content}</p>
                      </div>
                      <button
                        type="button"
                        className="shrink-0 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-irongrey transition-colors hover:border-primary/20 hover:bg-primary/5 hover:text-primary"
                        onClick={() => onOpenInChat(selectedSession.id, message.messageId)}
                        data-testid="ai-thread-jump-to-message"
                      >
                        Jump
                      </button>
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-[12px] text-irongrey/58">
                      <Clock3 size={12} />
                      <span>{formatTimestamp(message.createdAt)}</span>
                    </div>
                  </article>
                )) : null}
                {selectedTimelineViewState === 'empty' ? (
                  <div
                    className="rounded-[20px] border border-dashed border-slate-200 bg-[#f8faf9] px-4 py-4 text-sm text-irongrey/65"
                    data-testid="ai-threads-browser-detail-empty"
                  >
                    No persisted activity was found for this session yet.
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="flex h-full items-center justify-center rounded-[20px] border border-dashed border-slate-200 bg-[#f8faf9] px-4 py-8 text-sm text-irongrey/65">
          Select a thread to inspect its prompts, task checkpoints, and artifact moments.
        </div>
      )}
    </div>
  );

  return (
    <div
      ref={rootRef}
      className="relative min-h-[420px] overflow-hidden"
      data-testid="ai-threads-browser"
      data-layout={isTwoPane ? 'two-pane' : 'drawer'}
    >
      <div className={`grid min-h-[420px] gap-4 ${isTwoPane ? 'grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]' : 'grid-cols-1'}`}>
        <div
          className="flex min-h-0 flex-col overflow-hidden rounded-[24px] border border-slate-200/80 bg-white p-4 shadow-[0_18px_44px_rgba(36,50,53,0.06)]"
          data-testid="ai-threads-browser-list"
        >
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary/70">Threads</p>
              <h3 className="mt-1 text-sm font-semibold text-slate-900">All assistant sessions</h3>
            </div>
            <span className="rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-semibold text-primary">
              {sessions.length}
            </span>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto pr-1">
            <div className="space-y-3">
            {sessions.map((session) => {
              const isCurrent = session.id === currentSessionId;
              const isSelected = session.id === selectedSessionId;
              const preferredMessageId = getPreferredThreadAnchor(session);
              const previewSession = () => {
                onSelectSession(session.id);
                if (!isTwoPane) {
                  setDrawerSessionId(session.id);
                }
              };
              return (
                <div
                  key={session.id}
                  role="button"
                  tabIndex={0}
                  className={[
                    'flex w-full items-start justify-between gap-3 rounded-[20px] border px-4 py-3 text-left transition-colors',
                    isSelected
                      ? 'border-primary/25 bg-primary/5'
                      : 'border-slate-200/80 bg-[#f8faf9] hover:border-primary/20 hover:bg-white',
                  ].join(' ')}
                  onClick={() => {
                    onOpenInChat(session.id, preferredMessageId);
                  }}
                  onKeyDown={(event) => {
                    if (event.target !== event.currentTarget) {
                      return;
                    }
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onOpenInChat(session.id, preferredMessageId);
                    }
                  }}
                  data-testid="ai-thread-session-row"
                  data-current-session={isCurrent ? 'true' : 'false'}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-sm font-semibold text-slate-900">
                        {session.title ?? session.routeLabel ?? session.route}
                      </p>
                      {isCurrent ? (
                        <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold text-white">Current</span>
                      ) : null}
                    </div>
                    <p className="mt-1 line-clamp-2 text-xs leading-5 text-irongrey/68">
                      {session.lastAssistantPreview ?? session.lastMessagePreview ?? session.firstUserPreview ?? 'Session timeline available.'}
                    </p>
                    <div className="mt-2 flex items-center gap-2 text-[12px] text-irongrey/58">
                      <MessageSquareText size={12} />
                      <span>{session.messageCount}</span>
                      <Sparkles size={12} />
                      <span>{formatTimestamp(session.updatedAt)}</span>
                    </div>
                  </div>
                  <div className="mt-0.5 flex shrink-0 flex-col items-end gap-2">
                    <ArrowRight size={16} className="shrink-0 text-irongrey/45" />
                    <button
                      type="button"
                      className="inline-flex min-h-8 items-center rounded-full border border-slate-200 bg-white px-3 text-[11px] font-semibold text-irongrey/70 transition-colors hover:border-primary/25 hover:bg-primary/5 hover:text-primary"
                      onClick={(event) => {
                        event.stopPropagation();
                        previewSession();
                      }}
                      data-testid="ai-thread-session-preview"
                    >
                      Preview
                    </button>
                  </div>
                </div>
              );
            })}
            {sessions.length === 0 ? (
              <div className="rounded-[20px] border border-dashed border-slate-200 bg-[#f8faf9] px-4 py-4 text-sm text-irongrey/65">
                No assistant sessions have been persisted yet.
              </div>
            ) : null}
            </div>
          </div>
        </div>

        {isTwoPane ? detailPane : null}
      </div>

      {!isTwoPane && drawerSessionId ? (
        <div
          className="absolute inset-0 z-10 flex justify-end rounded-[24px] bg-white/10 p-2 backdrop-blur-[1px]"
          data-testid="ai-threads-browser-detail-drawer-shell"
        >
          <div className="relative h-full w-full max-w-[460px]">
            <button
              type="button"
              className="absolute right-4 top-4 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-irongrey/60 shadow-sm hover:text-primary"
              onClick={() => setDrawerSessionId(null)}
              data-testid="ai-threads-browser-detail-close"
              aria-label="Close thread detail"
            >
              <X size={16} />
            </button>
            {detailPane}
          </div>
        </div>
      ) : null}
    </div>
  );
};
